import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ShieldCheck, ScrollText, Scale, Award, Building2, Users, Landmark, ArrowRight, CheckCircle2 } from "lucide-react";
import { useCopy } from "@/contexts/LanguageContext";
import { DisclaimerBanner } from "@/components/DisclaimerBanner";

const IMAGES = {
  hero: "https://images.pexels.com/photos/31336015/pexels-photo-31336015.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=1800",
  msme: "https://images.pexels.com/photos/6949788/pexels-photo-6949788.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=900",
  bank: "https://images.pexels.com/photos/7580648/pexels-photo-7580648.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=900",
  gov: "/images/vallabh-bhawan.jpg",
};
const TRUST_ICONS = [Award, ShieldCheck, ScrollText, Scale];
const AUDIENCE = [
  { key: "msme", icon: Users, to: "/for-msmes", img: IMAGES.msme },
  { key: "bank", icon: Building2, to: "/for-banks", img: IMAGES.bank },
  { key: "gov", icon: Landmark, to: "/how-it-works", img: IMAGES.gov },
];
const TIER_BADGES = ["bg-slate-100 text-slate-800 border-slate-200", "bg-amber-100 text-amber-900 border-amber-300", "bg-emerald-100 text-emerald-900 border-emerald-300"];

const COPY = {
  en: {
    eyebrow: "MP MSME Credit Exchange",
    h1a: "One Financial Passport.", h1b: "Multiple lending", h1c: "opportunities.",
    sub: "Build a structured Financial Passport containing your business profile, financial information, funding requirement and supporting documents. With your consent, participating banks and NBFCs can review opportunities aligned with their lending policies and risk appetite.",
    cta1: "Check my business readiness", cta2: "Register as a lender",
    trustRow: ["One reusable Financial Passport", "Consent-controlled data sharing", "Policy-matched lender discovery", "End-to-end application tracking", "No guaranteed-approval claims"],
    stats: [["55", "MP districts covered"], ["10", "Central & MP schemes"], ["6", "Role-based portals"], ["16", "Clear application stages"]],
    trustEyebrow: "Why it matters", trustH: "Built for MSMEs, understood by banks.",
    trust: [
      ["One reusable Financial Passport", "A 12-section standardised credit file, completed once and reused for every match."],
      ["Consent-controlled sharing", "Full data reaches a lender only after the MSME's explicit, revocable consent."],
      ["Central & MP schemes", "Applicable central and state scheme routes, with clear next steps."],
      ["Compare complete impact", "Beyond interest rate — full cost, tenure, fees and documentation effort."],
    ],
    audEyebrow: "Who it serves", audH: "One exchange, three participants.",
    aud: {
      msme: ["For MSMEs", "Prepare once, understand your readiness and let suitable lenders find you.", "What MSMEs get"],
      bank: ["For banks & NBFCs", "Policy-matched, consent-backed opportunities — not unqualified leads.", "What lenders get"],
      gov: ["For government", "Aggregated district, sector and inclusion insight on MSME credit flow.", "See the full journey"],
    },
    tierEyebrow: "Three stages, not one", tierH: "A match is not a decision. We keep every stage visible.",
    tierP: "Loan Bazaar deliberately avoids a single generic \"approved\" badge. Every opportunity moves through three clearly separated stages so borrowers, lenders and RMs always know where they stand.",
    tiers: [
      ["Stage 01", "Suggested opportunity", "The platform identifies a possible lender or scheme based on the passport. This is not eligibility confirmation."],
      ["Stage 02", "Lender interest", "A lender indicates it is willing to review the MSME. This is not loan approval — the RM will now reach out."],
      ["Stage 03", "Formal application", "The lender begins its regulated appraisal. Sanction comes only when the lender communicates a formal decision."],
    ],
    ctaH: "Ready to see how bank-ready your business is?",
    ctaP: "Sign in with any of six demo roles — borrower, bank admin, RM, platform admin, facilitator or government monitor. Password",
    ctaLogin: "Try demo", ctaHow: "How it works",
  },
  hi: {
    eyebrow: "मध्य प्रदेश MSME क्रेडिट एक्सचेंज",
    h1a: "एक वित्तीय पासपोर्ट।", h1b: "अनेक ऋण", h1c: "अवसर।",
    sub: "अपने व्यवसाय प्रोफ़ाइल, वित्तीय जानकारी, ऋण आवश्यकता और सहायक दस्तावेज़ों के साथ एक संरचित वित्तीय पासपोर्ट बनाएँ। आपकी सहमति से, भागीदार बैंक और NBFC अपनी ऋण नीतियों और जोखिम क्षमता के अनुरूप अवसरों की समीक्षा कर सकते हैं।",
    cta1: "मेरे व्यवसाय की तत्परता जाँचें", cta2: "ऋणदाता के रूप में पंजीकरण करें",
    trustRow: ["एक पुन: प्रयोज्य वित्तीय पासपोर्ट", "सहमति-नियंत्रित डेटा साझाकरण", "नीति-आधारित ऋणदाता मिलान", "आवेदन की शुरू से अंत तक ट्रैकिंग", "स्वीकृति की कोई गारंटी का दावा नहीं"],
    stats: [["55", "मध्य प्रदेश के जिले"], ["10", "केंद्रीय एवं राज्य योजनाएँ"], ["6", "भूमिका-आधारित पोर्टल"], ["16", "स्पष्ट आवेदन चरण"]],
    trustEyebrow: "यह क्यों महत्वपूर्ण है", trustH: "MSME के लिए बना, बैंकों के लिए समझने योग्य।",
    trust: [
      ["एक पुन: प्रयोज्य वित्तीय पासपोर्ट", "12 खंडों वाली मानकीकृत क्रेडिट फ़ाइल — एक बार भरें, हर मिलान में उपयोग करें।"],
      ["सहमति-नियंत्रित साझाकरण", "पूर्ण डेटा ऋणदाता तक केवल MSME की स्पष्ट, वापस लेने योग्य सहमति के बाद पहुँचता है।"],
      ["केंद्रीय एवं मध्य प्रदेश योजनाएँ", "लागू केंद्रीय और राज्य योजनाओं के मार्ग, स्पष्ट अगले कदमों के साथ।"],
      ["पूरे प्रभाव की तुलना", "केवल ब्याज दर नहीं — कुल लागत, अवधि, शुल्क और दस्तावेज़ीकरण का प्रयास।"],
    ],
    audEyebrow: "यह किसके लिए है", audH: "एक एक्सचेंज, तीन भागीदार।",
    aud: {
      msme: ["MSME के लिए", "एक बार तैयारी करें, अपनी तत्परता समझें और उपयुक्त ऋणदाताओं को आपको खोजने दें।", "MSME को क्या मिलता है"],
      bank: ["बैंक एवं NBFC के लिए", "नीति-मिलान और सहमति-आधारित अवसर — असत्यापित लीड नहीं।", "ऋणदाताओं को क्या मिलता है"],
      gov: ["सरकार के लिए", "MSME ऋण प्रवाह पर जिला, क्षेत्र और समावेशन की समेकित जानकारी।", "पूरी प्रक्रिया देखें"],
    },
    tierEyebrow: "एक नहीं, तीन चरण", tierH: "मिलान निर्णय नहीं है। हम हर चरण को स्पष्ट रखते हैं।",
    tierP: "लोन बाजार जानबूझकर एक सामान्य \"स्वीकृत\" बैज से बचता है। हर अवसर तीन स्पष्ट रूप से अलग चरणों से गुजरता है, ताकि उधारकर्ता, ऋणदाता और RM हमेशा जानें कि वे कहाँ हैं।",
    tiers: [
      ["चरण 01", "सुझाया गया अवसर", "मंच पासपोर्ट के आधार पर संभावित ऋणदाता या योजना की पहचान करता है। यह पात्रता की पुष्टि नहीं है।"],
      ["चरण 02", "ऋणदाता की रुचि", "ऋणदाता MSME की समीक्षा करने की इच्छा दर्शाता है। यह ऋण स्वीकृति नहीं है — अब RM संपर्क करेगा।"],
      ["चरण 03", "औपचारिक आवेदन", "ऋणदाता अपना विनियमित मूल्यांकन शुरू करता है। स्वीकृति केवल ऋणदाता के औपचारिक निर्णय के बाद होती है।"],
    ],
    ctaH: "जानना चाहते हैं कि आपका व्यवसाय कितना बैंक-तैयार है?",
    ctaP: "छह डेमो भूमिकाओं में से किसी से साइन इन करें — उधारकर्ता, बैंक प्रशासक, RM, मंच प्रशासक, सुविधाकर्ता या सरकारी निगरानी। पासवर्ड",
    ctaLogin: "डेमो आज़माएँ", ctaHow: "यह कैसे काम करता है",
  },
};

const fade = (i = 0) => ({ initial: { opacity: 0, y: 16 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-60px" }, transition: { duration: 0.45, delay: i * 0.05 } });

function Hero({ c }) {
  return (
    <section className="relative overflow-hidden isolate" data-testid="hero-section">
      <div className="absolute inset-0">
        <img src={IMAGES.hero} alt="Indian MSME workshop" className="w-full h-full object-cover object-[60%_center] sm:object-center" loading="eager" />
        <div className="absolute inset-0 editorial-hero-scrim" />
        <div className="absolute inset-0 grain" />
      </div>
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 pt-20 pb-24 sm:pt-28 sm:pb-32 lg:pt-40 lg:pb-48">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} data-testid="hero-eyebrow"
          className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/25 backdrop-blur-sm px-3 py-1.5 text-[10px] sm:text-[11px] tracking-[0.16em] uppercase text-white/90">
          <span className="w-1.5 h-1.5 rounded-full" style={{ background: "#E86A2E" }} />{c.eyebrow}
        </motion.div>
        <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.05 }} data-testid="hero-headline"
          className="mt-6 sm:mt-8 max-w-4xl brand-heading text-white text-4xl sm:text-5xl lg:text-7xl leading-[1.05] tracking-tight">
          {c.h1a} <em className="italic text-[#F5B981] font-normal">{c.h1b}</em> {c.h1c}
        </motion.h1>
        <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.12 }} data-testid="hero-subheadline"
          className="mt-5 sm:mt-8 max-w-2xl text-base sm:text-lg text-white/80 leading-relaxed">{c.sub}</motion.p>
        <div className="mt-8 sm:mt-10 flex flex-wrap items-center gap-3">
          <Link to="/register" data-testid="hero-cta-primary" className="btn-primary !px-5 sm:!px-6">{c.cta1} <ArrowUpRight className="w-4 h-4" /></Link>
          <Link to="/for-banks" data-testid="hero-cta-secondary" className="btn-ghost-dark !px-5 sm:!px-6">{c.cta2}</Link>
        </div>
        <div className="mt-10 sm:mt-16 flex flex-wrap items-center gap-x-6 sm:gap-x-8 gap-y-3 text-[12px] sm:text-[13px] text-white/75" data-testid="hero-trust-row">
          {c.trustRow.map((tp) => <span key={tp} className="inline-flex items-center gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-[#7ED4A0]" /> {tp}</span>)}
        </div>
      </div>
    </section>
  );
}

function Stats({ c }) {
  return (
    <section className="border-b border-[color:var(--bk-line)]" style={{ background: "var(--bk-ivory-2)" }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-10 sm:py-14 grid grid-cols-2 md:grid-cols-4 gap-6 sm:gap-10">
        {c.stats.map(([n, l], i) => (
          <div key={i} data-testid={`stat-${i}`}>
            <div className="brand-heading text-4xl sm:text-5xl lg:text-6xl font-semibold text-[color:var(--bk-navy)] num-tabular leading-none">{n}</div>
            <div className="mt-2 sm:mt-3 text-[10px] sm:text-xs uppercase tracking-widest text-[color:var(--bk-navy)]/60">{l}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

function Trust({ c }) {
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-16 sm:py-24">
      <div className="max-w-2xl mb-10 sm:mb-14">
        <div className="text-[11px] uppercase tracking-[0.18em] text-[color:var(--bk-navy)]/60">{c.trustEyebrow}</div>
        <h2 className="mt-3 brand-heading text-3xl sm:text-4xl lg:text-5xl leading-[1.1] text-[color:var(--bk-navy)]">{c.trustH}</h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {c.trust.map(([title, desc], i) => {
          const Icon = TRUST_ICONS[i];
          return (
            <motion.div key={i} {...fade(i)} data-testid={`trust-card-${i}`} className="p-7 rounded-2xl bg-white border border-[color:var(--bk-line)] card-hover">
              <div className="w-10 h-10 rounded-xl bg-[color:var(--bk-navy)] grid place-items-center text-white"><Icon className="w-5 h-5" /></div>
              <div className="mt-6 brand-heading text-xl text-[color:var(--bk-navy)] leading-snug">{title}</div>
              <p className="mt-2 text-[15px] text-[color:var(--bk-navy)]/70 leading-relaxed">{desc}</p>
            </motion.div>
          );
        })}
      </div>
    </section>
  );
}

function Audience({ c }) {
  return (
    <section className="border-y border-[color:var(--bk-line)]" style={{ background: "#F7F2E7" }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-16 sm:py-24">
        <div className="text-[11px] uppercase tracking-[0.18em] text-[color:var(--bk-navy)]/60">{c.audEyebrow}</div>
        <h2 className="mt-3 brand-heading text-3xl sm:text-4xl lg:text-5xl leading-[1.1] text-[color:var(--bk-navy)]">{c.audH}</h2>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5">
          {AUDIENCE.map((a, i) => {
            const [title, body, link] = c.aud[a.key];
            const Icon = a.icon;
            return (
              <motion.div key={a.key} {...fade(i)}>
                <Link to={a.to} data-testid={`audience-${a.key}`} className="group block rounded-2xl overflow-hidden bg-white border border-[color:var(--bk-line)] card-hover h-full">
                  <div className="aspect-[16/10] overflow-hidden"><img src={a.img} alt={title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" /></div>
                  <div className="p-6">
                    <div className="flex items-center gap-2 text-[color:var(--bk-saffron)]"><Icon className="w-4 h-4" /><span className="text-[11px] uppercase tracking-[0.18em]">{title}</span></div>
                    <p className="mt-3 brand-heading text-lg leading-snug text-[color:var(--bk-navy)]">{body}</p>
                    <span className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-[color:var(--bk-navy)] group-hover:text-[color:var(--bk-saffron)]">{link} <ArrowRight className="w-4 h-4" /></span>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function Tiers({ c }) {
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-16 sm:py-24">
      <div className="max-w-3xl">
        <div className="text-[11px] uppercase tracking-[0.18em] text-[color:var(--bk-navy)]/60">{c.tierEyebrow}</div>
        <h2 className="mt-3 brand-heading text-3xl sm:text-4xl lg:text-5xl leading-[1.1] text-[color:var(--bk-navy)]">{c.tierH}</h2>
        <p className="mt-5 text-[color:var(--bk-navy)]/70 text-lg leading-relaxed">{c.tierP}</p>
      </div>
      <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="three-tier-distinction">
        {c.tiers.map(([tag, title, body], i) => (
          <div key={i} data-testid={`tier-stage-0${i + 1}`} className="p-6 rounded-2xl bg-white border border-[color:var(--bk-line)] card-hover">
            <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-widest ${TIER_BADGES[i]}`}>{tag}</span>
            <div className="mt-4 brand-heading text-xl text-[color:var(--bk-navy)]">{title}</div>
            <p className="mt-2 text-sm text-[color:var(--bk-navy)]/70 leading-relaxed">{body}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

export default function Home() {
  const c = useCopy(COPY);
  return (
    <div className="brand-body" data-testid="home-page">
      <Hero c={c} />
      <Stats c={c} />
      <Trust c={c} />
      <Audience c={c} />
      <Tiers c={c} />
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28">
        <div className="rounded-3xl border border-[color:var(--bk-line)] grid grid-cols-1 lg:grid-cols-12 gap-8 items-center p-10 lg:p-14" style={{ background: "var(--bk-ivory-2)" }}>
          <div className="lg:col-span-8">
            <h2 className="brand-heading text-3xl lg:text-4xl text-[color:var(--bk-navy)] leading-tight">{c.ctaH}</h2>
            <p className="mt-3 text-[color:var(--bk-navy)]/70 max-w-2xl">{c.ctaP} <code className="bg-white border border-[color:var(--bk-line)] px-1.5 py-0.5 rounded text-[13px]">Demo@123</code>.</p>
          </div>
          <div className="lg:col-span-4 flex lg:justify-end gap-3">
            <Link to="/login" data-testid="cta-login" className="btn-primary">{c.ctaLogin}</Link>
            <Link to="/how-it-works" data-testid="cta-how" className="inline-flex items-center gap-2 rounded-full border border-[color:var(--bk-navy)]/20 px-5 py-3 text-sm font-semibold text-[color:var(--bk-navy)] hover:border-[color:var(--bk-navy)]/50 transition-colors">{c.ctaHow}</Link>
          </div>
        </div>
        <div className="mt-8"><DisclaimerBanner /></div>
      </section>
    </div>
  );
}
